import React from 'react';
import { BUILD } from '@/lib/constants';

/**
 * <VersionBadge /> — small, classy chip showing the running build
 * version. Renders next to the "Vigilant Entities" wordmark in
 * Logo / LogoFull, the dashboard header, and the homepage eyebrow.
 *
 * Variants:
 *   - 'lcd'    (default v166): icy-cyan capsule with a digital/LCD
 *              tech-readout aesthetic — bold mono-numerals on a soft
 *              cyan plate with a subtle inset shadow. Inspired by
 *              the "toothrocket V1.0.55" treatment.
 *   - 'pill'   light violet capsule (legacy v164 style).
 *   - 'inline' faint inline text — minimal footprint.
 *   - 'subtle' uppercase tracked label — for footer-like contexts.
 *
 * Always wraps `data-testid="build-version-badge"` so QA / Playwright
 * can assert against it.
 */
export default function VersionBadge({
  variant = 'lcd',
  className = '',
  showDate = false,
  title,
}) {
  const label = showDate ? `${BUILD.version} · ${BUILD.date}` : BUILD.version;
  const tooltip = title || `Build ${BUILD.version} · ${BUILD.date} · ${BUILD.codename}`;

  const base = 'inline-flex items-center gap-1 select-none whitespace-nowrap';

  // Note: the 'lcd' variant deliberately uses an ALL-CAPS rendering for
  // the version string ("V1.6.4") to mimic a hardware readout. The
  // letter-spacing + heavy weight + faint inset shadow give it the
  // license-plate feel without being kitschy.
  const styles = {
    lcd:
      'font-mono font-bold uppercase ' +
      'text-[10px] tracking-[0.05em] ' +
      'px-2 py-[3px] rounded-[7px] ' +
      // Light cyan plate, slightly darker cyan border, deep-teal text.
      'bg-[#D8F1FB] text-[#1E7AA0] border border-[#9AD4EC]/80 ' +
      // Subtle inner shadow for the "screen" feel.
      'shadow-[inset_0_1px_0_rgba(255,255,255,0.7),inset_0_-1px_0_rgba(154,212,236,0.45)] ' +
      // Dark-mode: dim the plate so it doesn't scream off a black header.
      'dark:bg-[#0F2A36] dark:text-[#7BD3F5] dark:border-[#3F7E99]/60 ' +
      'dark:shadow-[inset_0_1px_0_rgba(123,211,245,0.10),inset_0_-1px_0_rgba(0,0,0,0.4)]',
    pill:
      'font-mono text-[10px] font-semibold px-2 py-0.5 rounded-full ' +
      'bg-[#7932AE]/10 text-[#5E1F8B] border border-[#7932AE]/25 ' +
      'dark:bg-white/[0.06] dark:text-[#C77CFF] dark:border-[#C77CFF]/30',
    inline:
      'font-mono text-[10px] font-semibold tracking-wider ' +
      'text-[#7932AE] dark:text-[#C77CFF]/85 opacity-80',
    subtle:
      'font-mono text-[9px] uppercase tracking-[0.18em] ' +
      'text-slate-500 dark:text-white/55',
  };

  // The lcd variant looks best with a leading capital "V"; legacy
  // variants keep the existing "v1.6.4" lowercase prefix.
  const display = variant === 'lcd'
    ? label.replace(/^v/i, 'V')
    : label;

  return (
    <span
      className={`${base} ${styles[variant] || styles.lcd} ${className}`}
      title={tooltip}
      aria-label={tooltip}
      data-testid="build-version-badge"
    >
      {display}
    </span>
  );
}
